import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { useTranslation } from '../i18n/LanguageContext';

const C = { green:'#10B981', dark:'#064E3B', navy:'#0D1B2A', grey:'#6B7280', border:'#E5E7EB', error:'#EF4444', bg:'#F0FDF4' };

const STEPS = ['Personal Details', 'Work Experience', 'Education & Qualifications', 'Review & Submit'];

const SPECIALIZATIONS = ['IELTS', 'Business English', 'Conversation', 'Grammar', 'Vocabulary', 'Reading & Writing', 'Kids / Young Learners', 'Exam Preparation', 'General English'];

function ProgressBar({ step }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:32 }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1 }}>
            <div style={{
              width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:700, fontSize:13,
              background: i < step ? C.green : i === step ? C.dark : '#E5E7EB',
              color: i <= step ? '#fff' : C.grey,
              transition:'all 0.3s'
            }}>
              {i < step ? '✓' : i + 1}
            </div>
            <div style={{ fontSize:11, color: i === step ? C.dark : C.grey, marginTop:4, fontWeight: i === step ? 700 : 400, textAlign:'center', maxWidth:80 }}>
              {label}
            </div>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ height:2, flex:1, background: i < step ? C.green : '#E5E7EB', marginBottom:20, transition:'background 0.3s', maxWidth:40 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function FileUpload({ label, hint, type, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type);
      const res = await api.post('/api/upload/tutor', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url, file.name);
    } catch (err) {
      setError('Upload failed. Please try again.');
    }
    setUploading(false);
  };

  const isImage = type === 'photo';

  return (
    <div style={{ marginBottom:20 }}>
      <label style={{ display:'block', fontWeight:600, fontSize:14, color:C.navy, marginBottom:6 }}>{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${value ? C.green : C.border}`, borderRadius:10, padding:20,
          textAlign:'center', cursor:'pointer', background: value ? '#ECFDF5' : '#FAFAFA',
          transition:'all 0.2s'
        }}
      >
        {value ? (
          <div style={{ color:C.green }}>
            {isImage
              ? <img src={value} alt="preview" style={{ width:80, height:80, borderRadius:'50%', objectFit:'cover', margin:'0 auto 8px' }} />
              : <div style={{ fontSize:32, marginBottom:8 }}>📄</div>
            }
            <div style={{ fontWeight:600, fontSize:13 }}>✅ Uploaded successfully</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>Click to replace</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize:28, marginBottom:8 }}>{isImage ? '📷' : type === 'cv' ? '📄' : '🎓'}</div>
            <div style={{ fontWeight:600, fontSize:13, color:C.grey }}>{uploading ? 'Uploading…' : `Click to upload ${label}`}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>{hint}</div>
          </>
        )}
      </div>
      {error && <div style={{ color:C.error, fontSize:12, marginTop:4 }}>{error}</div>}
      <input ref={inputRef} type="file" style={{ display:'none' }}
        accept={isImage ? 'image/*' : '.pdf,.doc,.docx,.jpg,.jpeg,.png'}
        onChange={handleFile} />
    </div>
  );
}

function Step1Personal({ data, setData }) {
  const update = (field, val) => setData(p => ({ ...p, [field]: val }));
  const inp = (field, extra={}) => ({
    value: data[field] || '',
    onChange: e => update(field, e.target.value),
    style: { width:'100%', padding:'11px 14px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' },
    ...extra
  });

  return (
    <div>
      <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:700, color:C.navy }}>Personal Details</h3>

      <FileUpload label="Profile Photo *" hint="JPG or PNG, max 5MB — shown on your ID card" type="photo"
        value={data.profile_photo_url} onChange={(url) => update('profile_photo_url', url)} />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div>
          <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>First Name *</label>
          <input {...inp('first_name')} placeholder="Jane" required />
        </div>
        <div>
          <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Last Name *</label>
          <input {...inp('last_name')} placeholder="Smith" required />
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Email Address *</label>
        <input {...inp('email')} type="email" placeholder="jane@example.com" required />
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Password *</label>
        <input {...inp('password')} type="password" placeholder="Minimum 8 characters" required />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div>
          <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Phone Number</label>
          <input {...inp('phone')} placeholder="+44 7700 900000" />
        </div>
        <div>
          <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Date of Birth</label>
          <input {...inp('date_of_birth')} type="date" />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div>
          <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Nationality</label>
          <input {...inp('nationality')} placeholder="British" />
        </div>
        <div>
          <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>Native Language</label>
          <input {...inp('native_language')} placeholder="English" />
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:8 }}>Specializations *</label>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {SPECIALIZATIONS.map(s => {
            const selected = (data.specializations || []).includes(s);
            return (
              <button key={s} type="button"
                onClick={() => update('specializations', selected
                  ? (data.specializations || []).filter(x => x !== s)
                  : [...(data.specializations || []), s]
                )}
                style={{ padding:'7px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                  background: selected ? C.green : '#F3F4F6', color: selected ? '#fff' : C.grey, transition:'all 0.15s' }}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label style={{ display:'block', fontWeight:600, fontSize:13, color:C.navy, marginBottom:6 }}>About You</label>
        <textarea value={data.bio || ''} onChange={e => update('bio', e.target.value)} rows={3}
          placeholder="Briefly describe your teaching experience and approach…"
          style={{ width:'100%', padding:'11px 14px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:14, resize:'none', boxSizing:'border-box' }} />
      </div>
    </div>
  );
}

function Step2Experience({ data, setData }) {
  const jobs = data.work_experience || [{ title:'', company:'', start:'', end:'', current:false, description:'' }];
  const update = (idx, field, val) => {
    const updated = jobs.map((j, i) => i === idx ? { ...j, [field]: val } : j);
    setData(p => ({ ...p, work_experience: updated }));
  };
  const addJob = () => setData(p => ({ ...p, work_experience: [...jobs, { title:'', company:'', start:'', end:'', current:false, description:'' }] }));
  const removeJob = (idx) => setData(p => ({ ...p, work_experience: jobs.filter((_, i) => i !== idx) }));

  const inp = (val, fn) => ({
    value: val || '',
    onChange: e => fn(e.target.value),
    style: { width:'100%', padding:'10px 12px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }
  });

  return (
    <div>
      <h3 style={{ margin:'0 0 6px', fontSize:18, fontWeight:700, color:C.navy }}>Work Experience</h3>
      <p style={{ margin:'0 0 20px', fontSize:13, color:C.grey }}>List your teaching or relevant professional experience (most recent first).</p>

      {jobs.map((job, idx) => (
        <div key={idx} style={{ border:`1.5px solid ${C.border}`, borderRadius:10, padding:18, marginBottom:16, background:'#FAFAFA' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>Position {idx + 1}</div>
            {jobs.length > 1 && (
              <button onClick={() => removeJob(idx)} style={{ background:'none', border:'none', color:C.error, cursor:'pointer', fontSize:13, fontWeight:600 }}>
                Remove
              </button>
            )}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Job Title *</label>
              <input {...inp(job.title, v => update(idx, 'title', v))} placeholder="English Teacher" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>School / Organisation *</label>
              <input {...inp(job.company, v => update(idx, 'company', v))} placeholder="City Language School" />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Start Date</label>
              <input type="month" {...inp(job.start, v => update(idx, 'start', v))} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>End Date</label>
              <input type="month" {...inp(job.end, v => update(idx, 'end', v))} disabled={job.current} />
            </div>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.grey, marginBottom:12, cursor:'pointer' }}>
            <input type="checkbox" checked={job.current || false} onChange={e => update(idx, 'current', e.target.checked)} />
            I currently work here
          </label>
          <div>
            <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Description</label>
            <textarea {...inp(job.description, v => update(idx, 'description', v))} rows={2}
              placeholder="Key responsibilities and achievements…"
              style={{ width:'100%', padding:'10px 12px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:13, resize:'none', boxSizing:'border-box' }} />
          </div>
        </div>
      ))}

      <button onClick={addJob} style={{ width:'100%', padding:'12px 0', border:`2px dashed ${C.border}`, borderRadius:8, background:'none', color:C.grey, fontSize:13, fontWeight:600, cursor:'pointer' }}>
        + Add Another Position
      </button>
    </div>
  );
}

function Step3Education({ data, setData }) {
  const edu = data.education || [{ degree:'', institution:'', year:'', field:'' }];
  const quals = data.qualifications || [{ name:'', issuer:'', year:'' }];

  const updateEdu = (idx, field, val) => {
    const updated = edu.map((e, i) => i === idx ? { ...e, [field]: val } : e);
    setData(p => ({ ...p, education: updated }));
  };
  const addEdu = () => setData(p => ({ ...p, education: [...edu, { degree:'', institution:'', year:'', field:'' }] }));
  const removeEdu = (idx) => setData(p => ({ ...p, education: edu.filter((_, i) => i !== idx) }));

  const updateQual = (idx, field, val) => {
    const updated = quals.map((q, i) => i === idx ? { ...q, [field]: val } : q);
    setData(p => ({ ...p, qualifications: updated }));
  };
  const addQual = () => setData(p => ({ ...p, qualifications: [...quals, { name:'', issuer:'', year:'' }] }));
  const removeQual = (idx) => setData(p => ({ ...p, qualifications: quals.filter((_, i) => i !== idx) }));

  const inp = (val, fn) => ({
    value: val || '',
    onChange: e => fn(e.target.value),
    style: { width:'100%', padding:'10px 12px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }
  });

  return (
    <div>
      {/* Education */}
      <h3 style={{ margin:'0 0 6px', fontSize:18, fontWeight:700, color:C.navy }}>Education</h3>
      <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>Add your academic qualifications.</p>

      {edu.map((e, idx) => (
        <div key={idx} style={{ border:`1.5px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:14, background:'#FAFAFA' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>Qualification {idx + 1}</div>
            {edu.length > 1 && <button onClick={() => removeEdu(idx)} style={{ background:'none', border:'none', color:C.error, cursor:'pointer', fontSize:12, fontWeight:600 }}>Remove</button>}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:10 }}>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Degree / Qualification</label>
              <input {...inp(e.degree, v => updateEdu(idx, 'degree', v))} placeholder="BA (Hons), PGCE…" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Field of Study</label>
              <input {...inp(e.field, v => updateEdu(idx, 'field', v))} placeholder="English Language Teaching" />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Institution</label>
              <input {...inp(e.institution, v => updateEdu(idx, 'institution', v))} placeholder="University of Oxford" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Year Completed</label>
              <input {...inp(e.year, v => updateEdu(idx, 'year', v))} placeholder="2018" maxLength={4} />
            </div>
          </div>
        </div>
      ))}
      <button onClick={addEdu} style={{ width:'100%', padding:'10px 0', border:`2px dashed ${C.border}`, borderRadius:8, background:'none', color:C.grey, fontSize:13, fontWeight:600, cursor:'pointer', marginBottom:28 }}>
        + Add Qualification
      </button>

      {/* Teaching Certifications */}
      <h3 style={{ margin:'0 0 6px', fontSize:18, fontWeight:700, color:C.navy }}>Teaching Certifications</h3>
      <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>CELTA, DELTA, TEFL, TESOL, etc.</p>

      {quals.map((q, idx) => (
        <div key={idx} style={{ border:`1.5px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:14, background:'#FAFAFA' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>Certificate {idx + 1}</div>
            {quals.length > 1 && <button onClick={() => removeQual(idx)} style={{ background:'none', border:'none', color:C.error, cursor:'pointer', fontSize:12, fontWeight:600 }}>Remove</button>}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Certificate Name *</label>
              <input {...inp(q.name, v => updateQual(idx, 'name', v))} placeholder="CELTA" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Issuing Body</label>
              <input {...inp(q.issuer, v => updateQual(idx, 'issuer', v))} placeholder="Cambridge" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:4 }}>Year</label>
              <input {...inp(q.year, v => updateQual(idx, 'year', v))} placeholder="2020" maxLength={4} />
            </div>
          </div>
        </div>
      ))}
      <button onClick={addQual} style={{ width:'100%', padding:'10px 0', border:`2px dashed ${C.border}`, borderRadius:8, background:'none', color:C.grey, fontSize:13, fontWeight:600, cursor:'pointer', marginBottom:28 }}>
        + Add Certificate
      </button>

      {/* File uploads */}
      <h3 style={{ margin:'0 0 16px', fontSize:18, fontWeight:700, color:C.navy }}>Upload Documents</h3>
      <FileUpload label="Proof of Qualification *" hint="Upload your highest teaching qualification (PDF, JPG, PNG)" type="qualification"
        value={data.qualification_doc_url} onChange={(url) => setData(p => ({ ...p, qualification_doc_url: url }))} />
      <FileUpload label="CV / Résumé" hint="PDF preferred — your work history and skills" type="cv"
        value={data.cv_url} onChange={(url) => setData(p => ({ ...p, cv_url: url }))} />
    </div>
  );
}

function Step4Review({ data }) {
  const Section = ({ title, children }) => (
    <div style={{ marginBottom:20, padding:16, background:'#F9FAFB', borderRadius:10, border:`1px solid ${C.border}` }}>
      <div style={{ fontWeight:700, fontSize:14, color:C.navy, marginBottom:10 }}>{title}</div>
      {children}
    </div>
  );
  const Row = ({ label, value }) => value ? (
    <div style={{ display:'flex', gap:8, marginBottom:6, fontSize:13 }}>
      <span style={{ color:C.grey, minWidth:130 }}>{label}:</span>
      <span style={{ color:C.navy, fontWeight:600 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div>
      <h3 style={{ margin:'0 0 6px', fontSize:18, fontWeight:700, color:C.navy }}>Review Your Application</h3>
      <p style={{ margin:'0 0 20px', fontSize:13, color:C.grey }}>Please check everything is correct before submitting.</p>

      <Section title="👤 Personal Details">
        {data.profile_photo_url && <img src={data.profile_photo_url} alt="Profile" style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', marginBottom:10 }} />}
        <Row label="Name" value={`${data.first_name || ''} ${data.last_name || ''}`.trim()} />
        <Row label="Email" value={data.email} />
        <Row label="Phone" value={data.phone} />
        <Row label="Nationality" value={data.nationality} />
        <Row label="Native Language" value={data.native_language} />
        <Row label="Specializations" value={(data.specializations || []).join(', ')} />
      </Section>

      <Section title="💼 Work Experience">
        {(data.work_experience || []).filter(j => j.title || j.company).map((j, i) => (
          <div key={i} style={{ marginBottom:10, paddingBottom:10, borderBottom: i < (data.work_experience||[]).length-1 ? `1px solid ${C.border}` : 'none' }}>
            <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>{j.title} — {j.company}</div>
            <div style={{ fontSize:12, color:C.grey }}>{j.start} {j.end ? `→ ${j.end}` : j.current ? '→ Present' : ''}</div>
          </div>
        ))}
        {!(data.work_experience || []).some(j => j.title) && <div style={{ color:C.grey, fontSize:13 }}>No experience added</div>}
      </Section>

      <Section title="🎓 Education & Qualifications">
        {(data.education || []).filter(e => e.degree).map((e, i) => (
          <div key={i} style={{ marginBottom:8 }}>
            <div style={{ fontWeight:600, fontSize:13 }}>{e.degree} {e.field ? `in ${e.field}` : ''}</div>
            <div style={{ fontSize:12, color:C.grey }}>{e.institution} · {e.year}</div>
          </div>
        ))}
        {(data.qualifications || []).filter(q => q.name).map((q, i) => (
          <div key={i} style={{ marginBottom:8 }}>
            <div style={{ fontWeight:600, fontSize:13 }}>📜 {q.name} — {q.issuer}</div>
            <div style={{ fontSize:12, color:C.grey }}>{q.year}</div>
          </div>
        ))}
        <Row label="Qual. Document" value={data.qualification_doc_url ? '✅ Uploaded' : '❌ Not uploaded'} />
        <Row label="CV" value={data.cv_url ? '✅ Uploaded' : 'Not uploaded'} />
      </Section>

      <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:10, padding:'14px 16px', fontSize:13, color:'#1D4ED8' }}>
        <strong>What happens next?</strong><br />
        Your application will be reviewed by the school admin, typically within 1–2 business days. If approved, you will receive an email and your tutor ID card will be issued automatically.
      </div>
    </div>
  );
}

export default function TutorRecruitment() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    first_name:'', last_name:'', email:'', password:'', phone:'',
    date_of_birth:'', nationality:'', native_language:'',
    bio:'', specializations:[],
    profile_photo_url:'',
    work_experience:[{ title:'', company:'', start:'', end:'', current:false, description:'' }],
    education:[{ degree:'', institution:'', year:'', field:'' }],
    qualifications:[{ name:'', issuer:'', year:'' }],
    cv_url:'', qualification_doc_url:'',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const validate = () => {
    if (step === 0) {
      if (!data.first_name || !data.last_name) return 'Please enter your full name';
      if (!data.email) return 'Email is required';
      if (!data.password || data.password.length < 8) return 'Password must be at least 8 characters';
      if (!(data.specializations||[]).length) return 'Please select at least one specialization';
    }
    if (step === 2) {
      if (!data.qualification_doc_url) return 'Please upload proof of your teaching qualification';
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      // Read tenant_id from URL query param (e.g. ?school=4) for school-specific links
      const params = new URLSearchParams(window.location.search);
      const schoolTenantId = params.get('school') || params.get('tenant_id');
      await api.post('/api/tutor/register', {
        ...data,
        specializations: (data.specializations||[]).map(s => s.toLowerCase().replace(/ /g,'_')),
        ...(schoolTenantId ? { tenant_id: parseInt(schoolTenantId, 10) } : {}),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  if (success) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }}
        style={{ background:'#fff', borderRadius:20, padding:48, textAlign:'center', maxWidth:480, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
        <h2 style={{ margin:'0 0 12px', fontSize:24, fontWeight:800, color:C.dark }}>Application Submitted!</h2>
        <p style={{ color:C.grey, fontSize:15, lineHeight:1.7, margin:'0 0 24px' }}>
          Thank you for applying to teach with SpeakIQ. Our admin team will review your application and get back to you within 1–2 business days.
        </p>
        <div style={{ background:C.bg, borderRadius:12, padding:20, marginBottom:24, textAlign:'left' }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.dark, marginBottom:10 }}>What happens next:</div>
          {['Your application is under review','Admin accepts or declines via their dashboard','If accepted, you receive a confirmation email','Your Tutor ID card is generated automatically','Log in at tutors.speakiq.uk to start teaching'].map((s, i) => (
            <div key={i} style={{ display:'flex', gap:10, marginBottom:8, fontSize:13, color:C.grey }}>
              <span style={{ color:C.green, fontWeight:700, minWidth:16 }}>{i+1}.</span> {s}
            </div>
          ))}
        </div>
        <a href="/login" style={{ display:'block', padding:'12px 0', background:C.green, color:'#fff', borderRadius:10, fontWeight:700, textDecoration:'none', fontSize:15 }}>
          Go to Tutor Login →
        </a>
      </motion.div>
    </div>
  );

  const STEP_COMPONENTS = [
    <Step1Personal data={data} setData={setData} />,
    <Step2Experience data={data} setData={setData} />,
    <Step3Education data={data} setData={setData} />,
    <Step4Review data={data} />,
  ];

  return (
    <div style={{ minHeight:'100vh', background:C.bg }}>
      {/* Header */}
      <div style={{ background:C.dark, color:'#fff', padding:'16px 32px', display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ fontWeight:800, fontSize:18 }}>SpeakIQ</div>
        <span style={{ opacity:0.4, fontSize:18 }}>›</span>
        <div style={{ opacity:0.8, fontSize:14 }}>Tutor Application</div>
      </div>

      <div style={{ maxWidth:680, margin:'0 auto', padding:'32px 24px' }}>
        <ProgressBar step={step} />

        {error && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
            style={{ background:'#FEF2F2', border:`1px solid ${C.error}30`, borderRadius:8, padding:'12px 16px', marginBottom:20, color:C.error, fontSize:13, fontWeight:600 }}>
            ⚠️ {error}
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-20 }}
            style={{ background:'#fff', borderRadius:16, padding:'28px 32px', boxShadow:'0 4px 24px rgba(0,0,0,0.06)' }}>
            {STEP_COMPONENTS[step]}
          </motion.div>
        </AnimatePresence>

        <div style={{ display:'flex', gap:12, marginTop:24 }}>
          {step > 0 && (
            <button onClick={() => { setStep(s => s-1); setError(''); }}
              style={{ flex:1, padding:'13px 0', background:'#fff', border:`1.5px solid ${C.border}`, borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer', color:C.grey }}>
              ← Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button onClick={next}
              style={{ flex:2, padding:'13px 0', background:C.green, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
              Continue →
            </button>
          ) : (
            <button onClick={submit} disabled={submitting}
              style={{ flex:2, padding:'13px 0', background:C.dark, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Submitting…' : '🚀 Submit Application'}
            </button>
          )}
        </div>

        <p style={{ textAlign:'center', fontSize:12, color:C.grey, marginTop:16 }}>
          Already applied?{' '}
          <a href="/login" style={{ color:C.green, fontWeight:600, textDecoration:'none' }}>Log in →</a>
        </p>
      </div>
    </div>
  );
}
